import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { AppFilterPanel, AppPageTabs } from '@components'
import { MY_BUSINESS_ROUTES } from '@constants/routes'
import { MbPage } from 'Pages/MyBusiness/_shared/Ui'

import ChannelMappingPage  from './ChannelMapping'
import OfficeMappingPage   from './OfficeMapping'
import CategoryMappingPage from './CategoryMapping'
import AgencyMappingPage   from './AgencyMapping'
import CheckoutConfigPage  from './CheckoutConfig'
import ExtrasBucketsPage   from './ExtrasBuckets'
import OfficeGroupsPage    from './OfficeGroups'
import PricelistGroupsPage from './PricelistGroups'
import CategoryMacrosPage  from './CategoryMacros'
import AgencyGroupsPage    from './AgencyGroups'

/**
 * Unified Admin landing page for My Business.
 *
 * Mapping admins + tenant config in a single tabbed view. Deep-linkable
 * via the `tab` query param so an external link can land directly on a
 * specific admin (e.g. `/my-business/admin?tab=checkout`).
 */
const TABS = [
	{ key: 'channels',   label: 'Channels',       element: <ChannelMappingPage  embedded/> },
	{ key: 'offices',    label: 'Offices',        element: <OfficeMappingPage   embedded/> },
	{ key: 'categories', label: 'Categories',     element: <CategoryMappingPage embedded/> },
	{ key: 'agencies',   label: 'Agencies',       element: <AgencyMappingPage   embedded/> },
	{ key: 'extras',     label: 'Extras Buckets', element: <ExtrasBucketsPage   embedded/> },
	{ key: 'office-groups', label: 'Office Groups', element: <OfficeGroupsPage embedded/> },
	{ key: 'pricelist-groups', label: 'Pricelist Groups', element: <PricelistGroupsPage embedded/> },
	{ key: 'agency-groups',    label: 'Agency Groups',   element: <AgencyGroupsPage embedded/> },
	{ key: 'category-macros',  label: 'Category Macros', element: <CategoryMacrosPage embedded/> },
	{ key: 'checkout',   label: 'CheckOut Config', element: <CheckoutConfigPage  embedded/> },
]

export default function MyBusinessAdminPage() {
	const [params, setParams] = useSearchParams()
	const current = params.get('tab') || TABS[0].key
	const tab     = TABS.find(t => t.key === current) || TABS[0]

	const handleChange = (_, key) => {
		setParams({ tab: key }, { replace: true })
	}

	return (
		<MbPage title={MY_BUSINESS_ROUTES.ADMIN_TITLE}
		        subtitle={MY_BUSINESS_ROUTES.ADMIN_SUBTITLE}>
			{/* The tabs in the white band of the page, as the app's Admin */}
			<AppFilterPanel sx={{ '& > .MuiBox-root': { pt: 2 } }}>
				<AppPageTabs value={tab.key}
				             onChange={handleChange}
				             stackProps={{ sx: { mb: 0 } }}
				             // Ten tabs in one row: tighter than the 40px between tabs elsewhere, and a sideways
				             // scroll instead of a second row when the screen is too narrow even so
				             variant="scrollable" scrollButtons="auto"
				             sx={{
					             maxWidth: 1,
					             '& .MuiTabs-flexContainer': { gap: '2px', flexWrap: 'nowrap' },
					             '& .MuiTabs-flexContainer > .MuiTab-root': { mr: 0, px: '10px', fontSize: 12.5 },
					             '& .MuiTabs-scrollButtons': { width: 28, flexShrink: 0 },
					             // The arrow only takes room when there is something to scroll to on its side
					             '& .MuiTabs-scrollButtons.Mui-disabled': { width: 0 },
				             }}
				             tabs={TABS.map(t => ({ key: t.key, value: t.key, label: t.label }))}/>
			</AppFilterPanel>

			{/* Each tab page is the existing standalone admin in `embedded`
			    mode — it skips wrapping itself in PageWrapper so the title
			    and tab strip aren't duplicated. */}
			{tab.element}
		</MbPage>
	)
}
